import React from 'react';
import PropTypes from 'prop-types';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation, Pagination, Scrollbar, A11y, Virtual } from 'swiper';
import FadeIn from 'react-fade-in';

import { CONTENT_PROPS } from '../shared/constants';

//#region //// COMPONENTS ////
import ContentCard from '../ContentCard';
//#endregion

//#region //// DUCKS ////
import * as hooks from './hooks';
import './styles.scss';
import { IMAGE_MODE } from '../../constants/imageMode';
import classNames from '../../utils/classnameUtils';
import { Nav } from 'react-bootstrap';
import { breakpointsWidth, loopAdditionalSlides, slidesBreakpoints } from './constants';
//#endregion

// install Swiper components
SwiperCore.use([Navigation, Pagination, Scrollbar, A11y, Virtual]);

function ContentListCarousel({
  contentCardProps = {},
  contentCollection = [],
  contentListID,
  getContentListHandler,
  imageMode = 'THUMB',
  title,
  textMode = 'inner',
  itemLabel = 'none',
  itemStyle,
  trailerOverview = false,
  playAndFavorite = false,
  selectedContentHandler = (c) => alert(`Selected Content : ${c.id}`),
  onClickPlayerButton = (c) => alert(`Selected Content Player: ${c}`),
  onClickMyListButton = (c) => alert(`Selected Content List: ${c}`),
  favorites = [],
  authenticated,
  accessToken,
  //isLoading(skeleton)
  isLoading = false,
  countItem = 6,
  contentNoImageProps,
  networks,
  channels,
  sectionLinked,
  handleSeeMore,
  translationTexts,
  lang,
  loadMoreDataInComponent,
  contentListDisplayItems,
  page
}) {

  const [slideActiveIndex, setSlideActiveIndex] = React.useState(0);
  if (contentCollection.length === 0 && isLoading) contentCollection.push(...new Array(countItem))

  //#region /////// HOOKS ///////
  hooks.useDidMount(contentListID, getContentListHandler, isLoading);
  const { slidePerViewCount } = hooks.useSlidePerViewCount(imageMode)
  //#endregion
  
  return (
    <section className={classNames("tbxContentListCarousel", `__imageMode-${imageMode.toLowerCase()}`, `__trailerOverview-${trailerOverview}`)}>
      <FadeIn>
        {!isLoading && ( sectionLinked && title ?
            <Nav.Link className="seeMore__content" onClick={() => handleSeeMore(sectionLinked?.url)}>
              <h2 className="tbxContentListCarousel__heading">{title} <i class="bi bi-chevron-right arrow"></i></h2>
            </Nav.Link>
            : <h2 className="tbxContentListCarousel__heading">{title}</h2>
        )}
        <Swiper
          className="tbxContentListCarousel__swiper"
          navigation={true}
          grabCursor={false}
          draggable={false}
          mousewheel={{
            forceToAxis: true
          }}
          loop={false}
          {...sizeGroupFunction(imageMode)}
          preventClicksPropagation={true}
          preventClicks={true}
          scrollbar={{ draggable: false, hide: true }}
          slideToClickedSlide={false}
          pagination={{ clickable: true }}
          onSlideChange={(swiper) => {
            setSlideActiveIndex(swiper.activeIndex)

            if (
              contentListDisplayItems !== null ||
              page.currentPage === page.totalPages ||
              (contentCollection.length - (slidePerViewCount.slidesPerView * 2) > swiper.activeIndex)
            )
              return;

            loadMoreDataInComponent && loadMoreDataInComponent()
          }}
          virtual
          slidesPerView={slidePerViewCount.slidesPerView}
        >
          {contentCollection.map((content, idx) => {
            const key = `tbxContentListCarousel-item-${idx}`
            return (
              <SwiperSlide
                onClick={() => {
                  if (!isLoading) selectedContentHandler(content)
                }}
                virtualIndex={key}
                key={key}
                className={classNames({
                  'tbxContentListCarousel__swiper-content': true,
                  [`__imageMode-${imageMode.toLowerCase()}`]: true,
                  'tbxContentListCarousel-loading': isLoading,
                  'tbxContentListCarousel-firstSlice': idx == slideActiveIndex,
                  'tbxContentListCarousel-lastSlice': (idx == (slideActiveIndex + (slidePerViewCount - 1)))
                })}
              >
                  <ContentCard
                    {...contentCardProps}
                    translationTexts={translationTexts}
                    content={content}
                    imageMode={imageMode}
                    textMode={textMode}
                    itemLabel={itemLabel}
                    itemStyle={itemStyle}
                    isLoading={isLoading}
                    playAndFavorite={playAndFavorite}
                    contentNoImageProps={contentNoImageProps}
                    networks={networks}
                    channels={channels}
                    trailerOverview={trailerOverview && ((slideActiveIndex + slidePerViewCount > idx) && (slideActiveIndex - 1 < idx))}
                    onClickPlayerButton={onClickPlayerButton}
                    onClickMyListButton={onClickMyListButton}
                    onClickSelectedContentButton={() => (!isLoading) && selectedContentHandler(content)}
                    favorites={favorites}
                    authenticated={authenticated}
                    accessToken={accessToken}
                    lang={lang}
                  />
              </SwiperSlide>
            );
          })
          }
        </Swiper>
      </FadeIn>
    </section >
  );
};

ContentListCarousel.propTypes = {
  contentCardProps: PropTypes.shape({
    prefixNetworkText: PropTypes.string
  }),
  contentCollection: PropTypes.arrayOf(PropTypes.shape(CONTENT_PROPS)),
  contentListID: PropTypes.string,
  getContentListHandler: PropTypes.func,
  imageMode: PropTypes.oneOf([IMAGE_MODE.POSTER, IMAGE_MODE.THUMB, IMAGE_MODE.SIMPLE_LARGE]),
  isLoading: PropTypes.bool,
  selectedContentHandler: PropTypes.func,
  title: PropTypes.string,
  contentNoImageProps: PropTypes.object.isRequired,
  networks: PropTypes.array,
  channels: PropTypes.array,
  sectionLinked: PropTypes.object,
  handleSeeMore: PropTypes.func,
  itemLabel: PropTypes.string

};

export default React.memo(ContentListCarousel);




export const sizeGroupFunction = (imageMode) => {
  const { width } = hooks.useResize();

  if (IMAGE_MODE.POSTER === imageMode || IMAGE_MODE.THUMB === imageMode || IMAGE_MODE.SIMPLE_LARGE === imageMode) {
    return {
      loopAdditionalSlides: (
        width >= breakpointsWidth.desktop ? loopAdditionalSlides[imageMode].desktop :
          width >= breakpointsWidth.tab_port ? loopAdditionalSlides[imageMode].tab_port :
            width >= breakpointsWidth.tab_medium ? loopAdditionalSlides[imageMode].tab_medium : loopAdditionalSlides[imageMode].phone),
      breakpoints: slidesBreakpoints[imageMode]
    }
  }
  
  return {}
}